// Map tunables (Leaflet). Tile source and attribution normally come from the backend (/meta).

export const MAP_TILES = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap',
  maxZoom: 18,
} as const;

/** Satellite basemap (Esri World Imagery, free with attribution). Note the {y}/{x} order. */
export const SATELLITE_TILES = {
  url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  attribution: 'Citra &copy; <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a>, Maxar, Earthstar Geographics',
  maxZoom: 18,
} as const;
export type Basemap = 'map' | 'satellite';

export const MAP_VIEW = {
  /** before any pin is known (West Java) */
  center: [-6.75, 108.4] as [number, number],
  zoom: 9,
  /** a map with a single location */
  singlePointZoom: 12,
  /** flying to a selected location never zooms out below this */
  focusMinZoom: 11,
  fitPadding: 56,
  /** the first view ("Tampilan awal") hugs the locations: a small margin, never closer than a city */
  homePadding: 36,
  homeMaxZoom: 12,
  fitAllMaxZoom: 13,
} as const;

/**
 * Mouse wheel. The full-screen command map zooms on a plain wheel (`free`), in quarter steps so it feels smooth;
 * maps inside a scrolling page leave the wheel to the page and zoom only with Ctrl/⌘ held (`modifier`).
 */
export const MAP_WHEEL = {
  /** zoom levels snap to this (fractional: smooth wheel and pinch, fits hug the pins closer) */
  zoomSnap: 0.25,
  /** the +/− buttons and the keyboard: one whole level per press, as before */
  zoomDelta: 1,
  /** wheel distance for one zoom level (Leaflet's default 60 jumps a level per notch) */
  wheelPxPerZoomLevel: 110,
  wheelDebounceTime: 30,
  /** the "hold Ctrl" hint stays this long after the last plain wheel turn (ms) */
  hintMs: 1_400,
} as const;
export type WheelMode = 'free' | 'modifier';

/** Map heights per view (px). */
export const MAP_HEIGHT = {
  overview: 470,
  locations: 420,
  cylinderRoute: 280,
  /** the route drawer of a Surat Jalan (= .trip-map-canvas.is-drawer), for its skeleton */
  tripDrawer: 420,
  locationDetail: 240,
} as const;

/** Trucks have no GPS: position = elapsed since departure over an assumed trip, capped before arrival. */
export const TRUCK_TRIP = {
  assumedMs: 3 * 3_600_000,
  maxProgress: 0.9,
  /** drawn at least this far along the line so it never sits on the origin pin */
  minDrawnProgress: 0.04,
} as const;

/** Maps narrower than this (px, phones) label only the selected and the warehouse pin. */
export const MAP_KEY_LABELS_BELOW = 600;

export const MAP_MARKERS = {
  /** pin size = base + sqrt(stock) * scale, capped; the warehouse is a little bigger */
  pinBase: 14,
  pinStockScale: 2.2,
  pinMax: 34,
  warehouseExtra: 4,
  truckZIndexOffset: 800,
  /** the fading trail behind a truck is at most this long (px), and never longer than the way driven */
  truckTrailPx: 150,
  /** cluster bubbles sit under single pins (the warehouse, the selected location) and trucks */
  clusterZIndexOffset: -1000,
  bumpScale: 1.35,
} as const;

export const MAP_LINES = {
  /** bend of the arcs between locations, and how many points sample them */
  arcBend: 0.18,
  arcSamples: 28,
  flowMinWeight: 1.5,
  flowExtraWeight: 3,
  tripWeight: 2.5,
  tripDash: '6 6',
  pulseWeight: 4,
  pulseMinSegment: 24,
  pulseSegmentShare: 0.18,
} as const;

/** "Ikuti kejadian": a location this close to the edge of the visible area counts as off screen (px). */
export const FOLLOW_EDGE_MARGIN = 48;

/**
 * National scale: while zoomed out, pins that fall in the same grid cell merge into one bubble (count of locations,
 * total stock, a red ring when one of them has a problem). The warehouse and the selected location stay pins.
 */
export const MAP_CLUSTER = {
  /** clustering starts from this many located locations (a regional network stays plain pins) */
  minPoints: 40,
  /** grid cell, screen px at the current zoom */
  cellPx: 80,
  /** neighbouring cells whose centres are closer than this (px) are joined into one bubble */
  mergePx: 72,
  /** from this zoom on every location is its own pin */
  pinsFromZoom: 11,
  /** pins and bubbles further than this share of the view outside it stay off the map (fewer markers) */
  keepOutsideView: 0.5,
  /** bubble diameter: base + sqrt(locations) * scale, capped (px) */
  sizeBase: 30,
  sizeScale: 2.4,
  sizeMax: 58,
} as const;

/** The map's region filter (kota/kabupaten from the location address) shows from this many located locations. */
export const REGION_FILTER_MIN_LOCATIONS = MAP_CLUSTER.minPoints;

/** = the backend's check (COORDINATES_OUTSIDE_INDONESIA): a location's point must lie inside this box. */
export const INDONESIA_BOUNDS = { south: -11.5, north: 6.5, west: 94.5, east: 141.5 } as const;

/** The location form's point picker. */
export const POINT_PICKER = {
  /** zoom when a point is set (a street: the pin can be placed on the right building) */
  pointZoom: 16,
  /** decimals kept for a coordinate (about 1 m) */
  decimals: 6,
  /** browser geolocation: wait at most this long for a fix (ms), accept one this old (ms) */
  geoTimeoutMs: 15_000,
  geoMaxAgeMs: 60_000,
  /** a device fix less precise than this (m, typical of a laptop's Wi-Fi/IP guess) is called rough */
  roughAccuracyM: 500,
  /** the pin already shows a point this close (degrees): the form rounds what the map gives it */
  samePointDeg: 1e-5,
  /** one arrow key moves the focused pin this far on screen (px); Shift multiplies it */
  keyStepPx: 8,
  keyStepShift: 5,
  /** the pin's drawing (.pick-pin): its box and the tip that marks the point (px) */
  pinSize: [34, 36] as [number, number],
  pinTip: [17, 34] as [number, number],
} as const;


/**
 * The overview map (Ruang Kendali): the server aggregates by zoom (GET /overview/map?level=), so the browser never
 * holds every Titik Distribusi. National zoom: one bubble per province; province zoom: per regency; close zoom: single
 * Titik Distribusi (clustered by the server where dense) and single trucks.
 */
export const OVERVIEW_MAP = {
  /** from this zoom: single Titik Distribusi and single trucks */
  pointsFromZoom: 8.5,
  /** ... when the box asked for is at most this wide and high (the server's limit is 4 degrees) */
  pointsMaxSpanDeg: 3.8,
  /** names next to Titik Distribusi only this close (phones: only the picked one) */
  labelsFromZoom: 13,
  /** a view change asks the server again once the map rested this long (ms) */
  viewDebounceMs: 250,
  /** the box asked for is the view grown by this share on every side (a small pan needs no new answer); the
   * server snaps it to its grid, so nearby views share one cached answer */
  bboxPad: 0.3,
  /** the whole country (south, west, north, east): the national view */
  indonesia: [-11.2, 94.8, 6.2, 141.2] as [number, number, number, number],
  fitPadding: 28,
  /** fits land on this zoom step (fractional: the country edge to edge); the +/- buttons step by `buttonStep` from
   * the half level below */
  fitZoomSnap: 0.05,
  buttonStep: 0.5,
  /** the control column at the right of the free map (44 px buttons, 12 px margin) */
  controlsPx: 56,
  /** the Klaster | Per provinsi switch at the free map's top left, and the legend's two rows at its bottom (px) */
  viewSwitchPx: 52,
  legendPx: 64,
  /** a phone-sized map: the country edge to edge, a small margin */
  fitPaddingNarrow: 8,
  fitMaxZoom: 13,
  /** never further out than the whole country fits on a phone */
  minZoom: 3,
  /** a trip shorter than this (ms) between departure and expected arrival is drawn as this long */
  minTripMs: 60_000,
  /** an area or cluster bubble: diameter grows with the square root of its cylinders, between these (px) */
  bubbleMin: 30,
  bubbleMax: 60,
  bubbleScale: 1.6,
  /** a Titik Distribusi dot (px) */
  spotSize: 14,
  /** a map narrower than this (px), or a zoom below `compactBelowZoom` (national), draws the compact province
   * pills: the emblem and a short number */
  narrowPx: 600,
  compactBelowZoom: 5,
} as const;

/** Couriers on a motorbike on the overview map (the trip canvas): Hub -> village, never a house. */
export const COURIER_DRAW = {
  /** a ride shorter than this on screen (px) counts in its Hub's badge instead */
  minChordPx: 28,
  bend: 0.18,
  lineWidth: 1.5,
  lineAlpha: 0.55,
  dash: [3, 4] as number[],
  dashFlowPxPerSec: 4,
  /** the motorbike: about 20 units long at this scale; a hovered one a little larger */
  bikeScale: 1.05,
  liftScale: 1.2,
  /** the motorbike inside a Hub's badge */
  badgeBikeScale: 0.62,
  hitPx: 14,
  hitTouchPx: 22,
  cullMarginPx: 30,
  /** badges closer than this (px) merge into one */
  mergePx: 44,
  /** a Hub's badge sits this far (px) right of and above its dot */
  badgeOffset: [8, -6] as [number, number],
  font: "'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', system-ui, sans-serif",
} as const;

/** The Statistik map: the steps of its sequential scale, the room around the regions it fits, how far it zooms in. */
export const REGION_MAP = { steps: 5, padPx: 36, maxZoom: 9, labelsFromZoom: 6, /** every child region in one answer (the API's page limit) */ rows: 100 } as const;

/** Overview bubbles that would overlap on screen are moved apart (px): the gap kept, the most one moves, the passes;
 * a "Per provinsi" pill is the emblem and its padding plus a width per character of its number. */
export const BUBBLE_SPREAD = { gapPx: 3, maxPx: 160, passes: 120, emblemPx: 34, digitPx: 7, pillPx: 26, leaderFromPx: 12 } as const;

/** Trucks on the overview map (one canvas layer): the arc, the glyph and how it moves. */
/** A single trip's map (a Surat Jalan page): room around both ends when it fits them, and how far it may zoom in. */
export const TRIP_MAP = { padPx: 36, maxZoom: 14 } as const;

export const TRIP_DRAW = {
  /** sideways bend of a trip's arc, as a share of its length */
  bend: 0.12,
  /** a truck stays this far (px) from the pins at either end */
  endGapPx: 14,
  /** a tap or click this close (px) picks a truck */
  hitPx: 16,
  hitTouchPx: 24,
  lineWidth: 2,
  dash: [5, 6] as number[],
  /** a route of several trucks (national zoom when the server sent counts only), on the road map */
  flowAlpha: 0.5,
  /** national and province zoom: many trucks share the view, their lines a little quieter (the picked one stays
   * strong), but still clearly visible on both basemaps */
  lightAlpha: 0.62,
  /** redraws while trucks glide (ms between frames: they move a pixel in seconds, 25 fps is smooth) */
  frameMs: 40,
  /** a new estimate from the server is reached gradually over this long (ms), never a jump */
  settleMs: 700,
  /** the truck icon: 24 units long drawn at this scale (about 26 px) */
  truckScale: 1.1,
  /** truck glyph scale at national / province zoom (1 at close zoom): still a readable truck */
  lightScale: 0.95,
  /** a route longer than this many times (view width + height) is drawn only where it crosses the view */
  longChordViews: 1.5,
  /** a trip this far (px) outside the view is not drawn */
  cullMarginPx: 40,
  /** a place name this close (px) to a truck is hidden, so trucks never cover text */
  labelClearPx: 16,
  /** names are checked for collisions this often (ms) while trucks move under them */
  labelCheckMs: 2_000,
  /** the soft disc under a picked or hovered truck (px) */
  haloPx: 19,
  /** the dashes of the way ahead flow towards the destination at this speed (one dash length a second) */
  dashFlowPxPerSec: 5,
  /** the short wake behind a truck: its length (px), how many fading steps, width and starting opacity */
  wakePx: 16,
  wakeSteps: 4,
  wakeWidth: 4,
  wakeAlpha: 0.28,
  /** with one truck picked or hovered, the others are drawn at this opacity */
  dimAlpha: 0.35,
} as const;
